import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PUBLIC_REPORT } from "@/constants/testIds";
import {
  Download, CheckCircle, AlertTriangle, AlertCircle,
  FileText, User, Calendar, Building2, Loader, Activity, Lock, Stethoscope
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function OverallBadge({ status }) {
  const config = {
    normal:              { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", icon: CheckCircle,    label: "All Normal" },
    requires_attention:  { bg: "bg-amber-50  border-amber-200",   text: "text-amber-700",   icon: AlertTriangle,  label: "Needs Attention" },
    critical:            { bg: "bg-red-50    border-red-200",     text: "text-red-700",     icon: AlertCircle,    label: "Critical" },
  };
  const c = config[status] || config.normal;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${c.bg} ${c.text}`}>
      <c.icon size={14} /> {c.label}
    </span>
  );
}

function MarkerRow({ marker, idx }) {
  const statusMap = {
    normal:    { row: "hover:bg-slate-50",       badge: "badge-normal" },
    abnormal:  { row: "bg-red-50 hover:bg-red-100",    badge: "badge-abnormal" },
    borderline:{ row: "bg-yellow-50 hover:bg-yellow-100", badge: "badge-borderline" },
  };
  const s = statusMap[marker.status] || statusMap.normal;
  // Strip verbose parenthetical Lipoprotein descriptions
  const displayName = (marker.name || "").replace(/\s*\([^)]*[Ll]ipoprotein[^)]*\)/g, "").trim();
  return (
    <tr className={`border-b border-slate-100 transition-colors ${s.row}`}>
      <td className="py-3 px-4 text-sm font-medium text-slate-900">{displayName}</td>
      <td className="py-3 px-4 text-sm font-mono text-slate-800 font-semibold">{marker.value}</td>
      <td className="py-3 px-4 text-sm text-slate-500">{marker.unit}</td>
      <td className="py-3 px-4 text-sm text-slate-500">{marker.reference_range}</td>
      <td className="py-3 px-4">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.badge}`}>
          {marker.status.charAt(0).toUpperCase() + marker.status.slice(1)}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 max-w-xs">{marker.explanation}</td>
    </tr>
  );
}

export default function PublicReportPage() {
  const { shareToken } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/shared/${shareToken}`)
      .then(({ data }) => setReport(data))
      .catch(() => setError("This report is not available. The link may have expired or sharing was disabled."))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/shared/${shareToken}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const pname = report?.patient_info?.name?.replace(/\s+/g, "_") || "patient";
      link.setAttribute("download", `blood_report_${pname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed. Please try again."); }
    finally { setDownloading(false); }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return iso; }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 text-center">
      <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <AlertCircle size={28} className="text-red-400" />
      </div>
      <h2 className="text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Report unavailable</h2>
      <p className="text-slate-400 text-sm max-w-xs mb-6">{error}</p>
      <Link to="/auth" className="text-blue-900 text-sm font-medium hover:underline">Sign in to LabReport AI</Link>
    </div>
  );

  const pi = report?.patient_info || {};
  const abnormalCount  = (report?.blood_markers || []).filter(m => m.status === "abnormal").length;
  const borderlineCount = (report?.blood_markers || []).filter(m => m.status === "borderline").length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid={PUBLIC_REPORT.page}>

      {/* Top banner */}
      <div className="bg-blue-900 text-white px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Activity size={14} className="text-white" />
            </div>
            <span className="font-semibold text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>LabReport AI</span>
            <span className="text-blue-300 text-xs hidden sm:inline">·</span>
            <span className="text-blue-200 text-xs hidden sm:inline">Shared Report (read-only)</span>
          </div>
          <Link
            data-testid={PUBLIC_REPORT.signUpBanner}
            to="/auth"
            className="flex items-center gap-1.5 bg-white text-blue-900 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0">
            <Lock size={11} /> Sign up free
          </Link>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              {pi.name || "Blood Test Report"}
            </h1>
            <p className="text-slate-400 text-sm">{report?.original_filename}</p>
          </div>
          <div className="flex items-center gap-3">
            <OverallBadge status={report?.overall_status} />
            <button
              data-testid={PUBLIC_REPORT.downloadButton}
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 bg-blue-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors disabled:opacity-60">
              {downloading ? <Loader size={15} className="animate-spin" /> : <Download size={15} />}
              {downloading ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </div>

        {/* Patient info cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { icon: User,      label: "Patient",      value: pi.name     || "Not provided" },
            { icon: Calendar,  label: "Date of Birth",value: pi.dob      || "Not provided" },
            { icon: Calendar,  label: "Test Date",    value: pi.test_date|| "Not provided" },
            { icon: Building2, label: "Laboratory",   value: pi.lab      || "Not provided" },
          ].map((item) => (
            <div key={item.label} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <item.icon size={13} className="text-slate-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</span>
              </div>
              <p className="text-sm font-medium text-slate-900">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{report?.blood_markers?.length || 0}</p>
            <p className="text-xs text-slate-400 mt-0.5">Markers tested</p>
          </div>
          <div className={`border rounded-lg p-4 text-center ${abnormalCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
            <p className={`text-2xl font-bold ${abnormalCount > 0 ? "text-red-700" : "text-slate-900"}`}>{abnormalCount}</p>
            <p className="text-xs text-slate-400 mt-0.5">Abnormal values</p>
          </div>
          <div className={`border rounded-lg p-4 text-center ${borderlineCount > 0 ? "bg-yellow-50 border-yellow-200" : "bg-white border-slate-200"}`}>
            <p className={`text-2xl font-bold ${borderlineCount > 0 ? "text-amber-700" : "text-slate-900"}`}>{borderlineCount}</p>
            <p className="text-xs text-slate-400 mt-0.5">Borderline values</p>
          </div>
        </div>

        {/* Overview */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <FileText size={18} className="text-blue-900" /> Overview
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed">{report?.overview}</p>
        </div>

        {/* Blood Markers Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Blood Test Results</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" /> Normal</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" /> Borderline</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" /> Abnormal</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid={PUBLIC_REPORT.markersTable}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Marker","Value","Unit","Reference","Status","Explanation"].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(report?.blood_markers || []).map((m, i) => <MarkerRow key={m.name || i} marker={m} idx={i} />)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Findings */}
        {report?.key_findings?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Key Findings</h2>
            <ul className="space-y-2">
              {report.key_findings.map((f, i) => (
                <li key={`finding-${i}`} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle size={14} className="text-blue-900 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Plain Language Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>What This Means For You</h2>
          <p className="text-slate-700 text-sm leading-relaxed">{report?.plain_language_summary}</p>
        </div>

        {/* Recommendations */}
        {report?.recommendations?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Medical Recommendations</h2>
            <ol className="space-y-3">
              {report.recommendations.map((r, i) => (
                <li key={`rec-${i}`} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-900 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <p className="text-sm text-slate-700 leading-relaxed">{r}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Doctor's Notes — read-only */}
        {report?.doctor_notes?.trim() && (
          <div data-testid="public-report-notes" className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Stethoscope size={17} className="text-amber-700" />
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Doctor's Notes
              </h2>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{report.doctor_notes}</p>
          </div>
        )}

        {/* CTA + disclaimer */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <div className="w-10 h-10 rounded-lg bg-blue-900 flex items-center justify-center mx-auto mb-3">
            <Activity size={18} className="text-white" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Analyze your own blood tests
          </h3>
          <p className="text-slate-400 text-sm mb-4">Upload any blood test PDF and get AI-powered insights in seconds.</p>
          <Link
            data-testid={PUBLIC_REPORT.ctaButton}
            to="/auth"
            className="inline-flex items-center gap-2 bg-blue-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors">
            Get started free
          </Link>
          <p className="text-xs text-slate-400 mt-4">
            This report is AI-generated for informational purposes only. Always consult a qualified healthcare professional.
            Analysis powered by Gemini Flash · Generated on {formatDate(report?.created_at)}
          </p>
        </div>
      </main>
    </div>
  );
}
